import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import t from 'prop-types';
import classNames from 'classnames';
import { EditorView } from 'codemirror';
import {
  Annotation,
  Compartment,
  EditorState,
  StateEffect,
  StateField,
} from '@codemirror/state';
import {
  crosshairCursor,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  lineNumbers,
  keymap,
  placeholder as placeholderFn,
  rectangularSelection,
  showPanel,
} from '@codemirror/view';
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completionKeymap,
  selectedCompletionIndex,
  setSelectedCompletion,
  startCompletion,
} from '@codemirror/autocomplete';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentMore,
  indentLess,
} from '@codemirror/commands';
import {
  bracketMatching,
  defaultHighlightStyle,
  foldGutter,
  foldKeymap,
  indentOnInput,
  syntaxHighlighting,
} from '@codemirror/language';
import {
  closeSearchPanel,
  highlightSelectionMatches,
  openSearchPanel,
  searchKeymap,
  searchPanelOpen,
} from '@codemirror/search';
import { theme } from './helpers/theme';
import {
  getLanguageName,
  getLanguageSyntax,
  getLanguageHelpText,
  buildLanguageExtensions,
} from './helpers/languages';
import { actionsGutter } from './helpers/actionsGutter';
import { buildPanelElement, InfoPanel, ShortcutsPanel } from './helpers/panels';
import { ExpandableModal } from '../ExpandableModal';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { IS_MAC } from '../../utils';
import { fromJS, is, Map, OrderedMap } from 'immutable';

// Define custom annotation to use when the value of the editor is changed
// externally, so we can prevent the onChange callback from triggering.
const External = Annotation.define();

/**
 * Custom hook that builds all the extensions that are used by the CodeMirror
 * editor to customize its functionality to our needs, memoizing the different
 * extensions individually so they don't needlessly update.
 *
 * @param {object} options
 * @returns {array}
 */
function useExtensions({
  actionsGutterElement,
  bindings,
  className,
  disabled = false,
  language,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  infoPanelElement,
  shortcutsPanelState,
  shortcutsPanelToggleEffect,
  simple,
  toggleSearch,
}) {
  // Create an array to track changed extensions
  let extensionsUpdates = [];

  // Build extensions for the correct language and its completions
  const languageExtensions = useMemo(() => {
    extensionsUpdates.push('languageExtensions');
    return buildLanguageExtensions(language, bindings, disabled);
  }, [language, bindings, disabled]);

  // Build update listener extension that triggers the on change event when the
  // content of the code editor changes, and triggers the onFocus and onBlur
  // events when the field's focus changes
  const changeExtension = useMemo(() => {
    extensionsUpdates.push('changeExtension');
    return EditorView.updateListener.of(update => {
      if (
        update.docChanged &&
        !disabled &&
        // Don't trigger onChange if the value was changed externally
        !update.transactions.some(tr => tr.annotation(External))
      ) {
        onChange(update.state.doc.toString());
      } else if (update.focusChanged && !disabled) {
        if (update.view.hasFocus) {
          // Set the lastFocusTime internal state when the field is focused
          update.view.inputState.lastFocusTime = Date.now();
          typeof onFocus === 'function' && onFocus();
        } else typeof onBlur === 'function' && onBlur();
      }
    });
  }, [onChange, onBlur, onFocus, disabled]);

  // Build extensions to set classes on the editor element
  const classExtension = useMemo(() => {
    extensionsUpdates.push('classExtension');
    return [
      EditorView.editorAttributes.of({
        class: classNames(className, { 'cm-disabled': disabled }),
      }),
      // Disable cursor blinking when the editor is disabled
      disabled && drawSelection({ cursorBlinkRate: 0 }),
    ].filter(Boolean);
  }, [className, disabled]);

  // Build placeholder extension
  const placeholderExtension = useMemo(() => {
    extensionsUpdates.push('placeholderExtension');
    return typeof placeholder === 'string' ? placeholderFn(placeholder) : [];
  }, [placeholder]);

  // Build actions gutter extension
  const actionsGutterExtension = useMemo(() => {
    extensionsUpdates.push('actionsGutterExtension');
    // Only render this extension when we're in simple mode
    if (!simple) return [];
    return [actionsGutter(actionsGutterElement)];
  }, [simple, actionsGutterElement]);

  // Build info panel extension
  const infoPanelExtension = useMemo(() => {
    extensionsUpdates.push('infoPanelExtension');
    // Only render this extension when we're not in simple mode
    if (simple) return [];
    return showPanel.of(() => ({ dom: infoPanelElement, top: false }));
  }, [simple, infoPanelElement]);

  // Build shortcuts panel extension
  const shortcutsPanelExtension = useMemo(() => {
    // Don't render the shortcuts panel for simple view
    extensionsUpdates.push('shortcutsPanelExtension');
    // Only render this extension when we're not in simple mode
    if (simple) return [];
    return [
      shortcutsPanelState,
      keymap.of([
        // Toggle shortcuts panel with F1
        {
          key: 'F1',
          run(view) {
            const newStateValue = !view.state.field(shortcutsPanelState);
            view.dispatch({
              effects: shortcutsPanelToggleEffect.of(newStateValue),
            });
            // When opening shortcuts panel, close search panel
            if (newStateValue) closeSearchPanel(view);
            return true;
          },
        },
      ]),
    ];
  }, [simple, shortcutsPanelState, shortcutsPanelToggleEffect]);

  // Build extension to set the read only state of the editor
  const readOnlyExtension = useMemo(() => {
    extensionsUpdates.push('readOnlyExtension');
    return EditorState.readOnly.of(disabled);
  }, [disabled]);

  // Build extension to override keybindings
  const keymapExtension = useMemo(() => {
    extensionsUpdates.push('keymapExtension');
    return keymap.of([
      // Override search panel keybind so we can use our own function
      {
        key: 'Mod-f',
        run: () => {
          // Only toggle the search if not in simple view
          !simple && toggleSearch();
          return true;
        },
      },
    ]);
  }, [simple, toggleSearch]);

  // Define static extensions that are only available not in simple mode
  const nonSimpleExtensions = useMemo(() => {
    extensionsUpdates.push('nonSimpleExtensions');
    return !simple
      ? [
          // Gutter and panel extensions
          lineNumbers(),
          highlightActiveLineGutter(),
          foldGutter(),
          highlightSelectionMatches(),
          // Add additional keybindings
          keymap.of([
            ...searchKeymap,
            ...foldKeymap,
            // Allow Tab key to indent inside the code editor, unless it's pressed
            // within 500ms of the field gaining focus, in which case Tab will move
            // focus to the next available element
            {
              key: 'Tab',
              run: view => {
                if (Date.now() > view.inputState.lastFocusTime + 500) {
                  return indentMore(view);
                }
              },
              shift: view => {
                if (Date.now() > view.inputState.lastFocusTime + 500) {
                  return indentLess(view);
                }
              },
            },
          ]),
        ]
      : [];
  }, [simple]);

  // Define extensions that don't have any dependencies and will remain the
  // same after they are initialized
  const staticExtensions = useMemo(
    () => [
      // Use all the extensions from the basic setup
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      bracketMatching(),
      closeBrackets(),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      // Add our theme
      theme,
      // Extension to disable autocompletion icons.
      // Use `closeOnBlur: false` to be able to inspect the completions tooltip.
      autocompletion({ icons: false }),
      // Enable line wrapping in the editor
      EditorView.lineWrapping,
      // Add additional keybindings
      keymap.of([
        // Keymaps from the basic setup
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...completionKeymap,
        // Trigger custom event that will be used to expand/collapse the editor
        {
          key: 'Mod-m',
          run(view) {
            view.dom.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'CodeEditorToggle',
                bubbles: true,
              }),
            );
            return true;
          },
        },
      ]),
    ],
    [],
  );

  return [
    // Order matters for extension execution
    OrderedMap({
      keymapExtension,
      staticExtensions,
      nonSimpleExtensions,
      actionsGutterExtension,
      languageExtensions,
      changeExtension,
      infoPanelExtension,
      shortcutsPanelExtension,
      classExtension,
      placeholderExtension,
      readOnlyExtension,
    }),
    extensionsUpdates,
  ];
}

export const CodeEditor = props => {
  const {
    allowCopy,
    bindings: bindingsProp,
    className,
    focusRef,
    id: origId,
    language,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    simple: simpleProp = false,
    title,
    value,
    wrapperClassName,
  } = props;
  // Validate that the id only has valid characters
  const id = origId?.replaceAll(/[^\w-]/g, '__');
  const disabled = props.disabled || typeof onChange !== 'function';

  // DOM element into which we will render the CodeEditor. This uses useState
  // and a function ref because we only want to load the CodeEditor once, which
  // is when we first get the DOM element.
  const [element, setElement] = useState();
  const ref = useCallback(node => {
    if (!node) return;
    setElement(node);
  }, []);
  // The view object for the CodeMirror library
  const [view, setView] = useState();
  // A map of CodeMirror compartments that will be used to wrap extensions
  const [compartments, setCompartments] = useState(Map());

  // State to use for when to use the simple UI
  const [simple, setSimple] = useState(simpleProp);
  // Create a toggle function for the simple state, which will only keep
  // simple mode on when the editor is not expanded.
  const toggleSimpleOnExpand = useCallback(
    open => {
      setSimple(!open);
      // Close the search panel when we collapse the editor since it's not
      // supposed to be accessible in simple mode
      !open && closeSearchPanel(view);
    },
    [view],
  );

  // The bindings object passed in is a different object, so we'll store it in
  // state, and us the useEffect hook to only update it if there are actual
  // changes to the bindings content
  const [rawBindings, setRawBindings] = useState(bindingsProp);
  useEffect(() => {
    if (!is(fromJS(bindingsProp), fromJS(rawBindings))) {
      setRawBindings(bindingsProp);
    }
  }, [bindingsProp]);

  // Process the bindings and memoize the result
  const bindings = useMemo(() => processBindings(rawBindings), [rawBindings]);

  // State to store the DOM elements of our custom panels.
  const [infoPanelElement] = useState(buildPanelElement('info'));
  const [shortcutsPanelElement] = useState(buildPanelElement('shortcuts'));
  const [actionsGutterElement] = useState(
    // The buildPanelElement function just creates a dom element so we're
    // reusing it here even though this won't be a panel
    buildPanelElement('actions-gutter-content'),
  );

  // Custom CodeMirror StateField object to store a state value for the
  // shortcuts panel so we can toggle it
  const [[shortcutsPanelToggleEffect, shortcutsPanelState]] = useState([
    StateEffect.define(),
    StateField.define({
      create: () => false,
      update(value, tr) {
        const effect = tr.effects.findLast(effect =>
          effect.is(shortcutsPanelToggleEffect),
        );
        if (effect) return effect.value;
        else return value;
      },
      provide: field => {
        return showPanel.from(field, open =>
          open ? () => ({ dom: shortcutsPanelElement, top: false }) : null,
        );
      },
    }),
  ]);

  // Handler for toggle the search panel open/closed
  const toggleSearch = useCallback(() => {
    if (view) {
      if (searchPanelOpen(view.state)) {
        closeSearchPanel(view);
      } else {
        openSearchPanel(view);
        // When opening search panel, close the shortcuts panel
        view.dispatch({ effects: shortcutsPanelToggleEffect.of(false) });
      }
    }
  }, [view, shortcutsPanelToggleEffect]);

  // Toggle function for shortcuts panel
  const toggleShortcuts = useCallback(
    value => {
      const newStateValue =
        typeof value === 'boolean'
          ? value
          : !view.state.field(shortcutsPanelState);
      view.dispatch({ effects: shortcutsPanelToggleEffect.of(newStateValue) });
      // When opening shortcuts panel, close the search panel
      if (newStateValue) closeSearchPanel(view);
    },
    [view, shortcutsPanelToggleEffect, shortcutsPanelState],
  );

  // Function that focuses the editor and triggers the completions menu
  const triggerAutocompletions = useCallback(() => {
    view.focus();
    startCompletion(view);
  }, [view]);

  // Build the extensions needed for the editor
  const [extensionsMap, extensionsUpdates] = useExtensions({
    actionsGutterElement,
    bindings,
    className,
    disabled,
    language,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    infoPanelElement,
    shortcutsPanelState,
    shortcutsPanelToggleEffect,
    simple,
    toggleSearch,
  });

  // If any extensions were updated, update the view accordingly
  if (view && extensionsUpdates.length > 0) {
    view.dispatch({
      effects: extensionsUpdates.map(name =>
        compartments.get(name).reconfigure(extensionsMap.get(name)),
      ),
    });
  }

  // Initialize and load the CodeMirror editor
  useEffect(() => {
    if (!element) return;

    // Create a compartment for each extension and store the compartments in
    // state so we can update them when their extensions change
    const compartmentsMap = extensionsMap.map((ext, name) =>
      compartments.get(name, new Compartment()),
    );
    setCompartments(compartmentsMap);

    // Create the editor state
    let state = EditorState.create({
      // Pass in initial value for the editor
      doc: value,
      // Configure all extensions
      extensions: extensionsMap
        // Initialize the extensions using their compartments
        .map((extension, name) => compartmentsMap.get(name).of(extension))
        .toList()
        .toArray(),
    });

    // Create the editor view
    const view = new EditorView({ state, parent: element });

    // Set view in state so we can access from outside
    setView(view);

    // Add autocomplete tooltip event listeners by using a MutationObserver
    // to find when the tooltip is opened so we can add the listeners. This
    // function returns a function that should be called when the view is
    // destroyed.
    const autocompleteObserver = addAutocompleteObserver(view);

    // Cleanup the view and observer when the element changes
    return () => {
      autocompleteObserver.disconnect();
      view.destroy();
    };
  }, [element]);

  useEffect(() => {
    // Since the editor isn't a standard input, we can't pass in the focusRef
    // to it, so instead if the focusRef prop is provided, we'll focus the
    // editor.
    if (!!view && !!focusRef) view.focus();
  }, [view, focusRef]);

  // Update state of code editor if the value changes externally
  useEffect(() => {
    if (value === undefined) return;
    const currentValue = view ? view.state.doc.toString() : '';
    if (view && value !== currentValue) {
      view.dispatch({
        // Replace entire current value with the value passed into the props
        changes: { from: 0, to: currentValue.length, insert: value || '' },
        annotations: [External.of(true)],
      });
    }
  }, [value, view]);

  return (
    <ExpandableModal onToggle={simpleProp && toggleSimpleOnExpand}>
      {({ Expandable, expandableProps, toggle, isOpen }) => (
        <>
          <Expandable
            {...expandableProps}
            title={title}
            className="flush expandable-modal--code-editor"
          >
            <div
              ref={ref}
              className={classNames('code-editor', wrapperClassName, {
                'code-editor--full': isOpen,
              })}
              id={id}
              // Prevent Escape from propagating from within the code editor to
              // prevent unintended actions such as a modal getting closed.
              onKeyDown={e => {
                preventEscapePropagation(e);
                // If a keyDown event with a custom CodeEditorToggle key is
                // dispatched, toggle the code editor
                if (e.key === 'CodeEditorToggle') toggle();
              }}
              onKeyUp={preventEscapePropagation}
            >
              {/* Render a placeholder element with the content while the editor 
              isn't initialized to prevent the page from jumping when it loads*/}
              {!element && <pre>{value} </pre>}
            </div>
          </Expandable>
          {infoPanelElement &&
            // Render the info panel using a React portal into the dom element
            // created for the panel via the extensions, but only if the editor
            // is expanded or not in simple mode
            createPortal(
              <InfoPanel
                editorId={id}
                language={getLanguageName(language)}
                languageSyntax={getLanguageSyntax(language)}
                languageHelpText={getLanguageHelpText(language)}
                expand={toggle}
                isExpanded={isOpen}
                toggleSearch={toggleSearch}
                copyValue={
                  view &&
                  (allowCopy === true || (allowCopy !== false && disabled))
                    ? view.state.doc.toString()
                    : null
                }
                toggleShortcuts={!disabled && toggleShortcuts}
                triggerAutocompletions={
                  !disabled && view && triggerAutocompletions
                }
              />,
              infoPanelElement,
            )}
          {shortcutsPanelElement &&
            // Render the info panel using a React portal into the dom element
            // created for the panel via the extensions
            createPortal(
              <ShortcutsPanel toggle={toggleShortcuts} />,
              shortcutsPanelElement,
            )}
          {simple &&
            actionsGutterElement &&
            // Render an expand button using a React portal into the dom
            // element created for the actions gutter, which is used when the
            // CodeEditor is in simple mode
            createPortal(
              <Tooltip
                content={
                  <div className="shortcut-item">
                    <span>Maximize</span>
                    <span className="keys">
                      <span className="key">{IS_MAC ? '\u2318' : 'Ctrl'}</span>
                      <span className="key">M</span>
                    </span>
                  </div>
                }
                container={id ? `#${id}` : '.code-editor'}
                className="tooltip-light"
                placement="left"
                delay={{ show: 200 }}
                strategy="fixed"
              >
                {({ ref }) => (
                  <button
                    ref={ref}
                    type="button"
                    className="btn btn-xs"
                    onClick={toggle}
                    aria-label="Maximize Editor"
                  >
                    <Icon name="maximize-2" sm />
                  </button>
                )}
              </Tooltip>,
              actionsGutterElement,
            )}
        </>
      )}
    </ExpandableModal>
  );
};
function preventEscapePropagation(e) {
  if (e.key === 'Escape') {
    e.stopPropagation();
    e.preventDefault();
  }
}

/**
 * Sets up a MutationObserver to track when the autocompletion tooltip is added
 * to or removed from the dom. When it's added, an mouseover event is added to
 * the completions so hovering over them will select them in order to show
 * proper hover styling and show any configured info text.
 *
 * @param view The CodeMirror view object
 * @returns {MutationObserver} The observer, which needs to be disconnected
 *    when the editor is unmounted.
 */
function addAutocompleteObserver(view) {
  // Define the mouseover listener for updating the selected completion when we
  // hover over it
  const onCompletionHover = e => {
    const li = e.target.closest('li');
    if (li) {
      // If we're hovering over a completion other than the selected one,
      // select the completion we're hovering over
      const selectedIndex = selectedCompletionIndex(view.state);
      const hoverIndex = Array.from(li.parentNode.children)
        .filter(el => el.matches('li'))
        .indexOf(li);
      if (selectedIndex !== hoverIndex) {
        view.dispatch({ effects: setSelectedCompletion(hoverIndex) });
      }
    }
  };

  // Define the mousedown event that we'll use to prevent the tooltip from
  // closing when the user clicks on the scrollbar or a section header
  const onParentClick = e => {
    if (e.target.matches('ul') || e.target.matches('completion-section')) {
      e.preventDefault();
    }
  };

  // Mutation callback that checks for the autocomplete tooltip to be added
  // to the dom, and then adds the appropriate event listeners to it
  const mutationCallback = mutationList => {
    for (const mutation of mutationList) {
      // If the autocomplete tooltip was added
      const addedTooltip = Array.from(mutation.addedNodes).find(e =>
        e.classList.contains('cm-tooltip-autocomplete'),
      );
      if (addedTooltip) {
        // Add mouse over listener for selecting completions with a mouse
        addedTooltip.addEventListener('mouseover', onCompletionHover);
        // Add mouse down listener to prevent closing when the wrapper is clicked
        addedTooltip.addEventListener('mousedown', onParentClick);
      }

      // If the autocomplete tooltip was removed
      const removedTooltip = Array.from(mutation.removedNodes).find(e =>
        e.classList.contains('cm-tooltip-autocomplete'),
      );
      if (removedTooltip) {
        // Remove any added listeners
        removedTooltip.removeEventListener('mouseover', onCompletionHover);
        removedTooltip.removeEventListener('mousedown', onParentClick);
      }
    }
  };
  // Create an observer instance linked to the callback function
  const observer = new MutationObserver(mutationCallback);
  // Start observing the target node for configured mutations
  observer.observe(view.dom, { childList: true });
  // Return the observer so it can be disconnected in the future
  return observer;
}

/**
 * Takes the provided bindings and recursively processes them into the structure
 * needed by the code editor. This mostly copies some properties from the parent
 * to its children, since the children need to know about things such as the
 * parent type so they can properly apply closing syntax for things like
 * functions and brackets.
 *
 * @param {object[]} bindings
 *    An array of objects describing potential autocompletion options.
 * @param {object} addProps
 *    Object of props to add to the bindings. Used to copy props from a parent
 *    to its children or siblings.
 *
 * @returns {object[]}
 *    Returns the same list of bindings, but adds additional properties to
 *    nested bindings.
 *    Children of bindings get `parentType` and `parentQuoteType` prop set to
 *    the values of their parent's corresponding type and quoteType properties.
 *    Siblings of bindings get `type`, `quoteType`, `parentType`,
 *    `parentQuoteType`, and `siblingSeparator` inherited from their parent, and
 *    get `isSibling` set to true.
 */
function processBindings(bindings = [], addProps = {}) {
  return Array.isArray(bindings)
    ? bindings.map(binding => {
        const updatedBinding = { ...binding, ...addProps };
        return {
          ...updatedBinding,
          children: Array.isArray(updatedBinding.children)
            ? processBindings(updatedBinding.children, {
                parentType: updatedBinding.type,
                parentQuoteType: updatedBinding.quoteType,
              })
            : undefined,
          siblings: Array.isArray(updatedBinding.siblings)
            ? processBindings(updatedBinding.siblings, {
                type: updatedBinding.type,
                quoteType: updatedBinding.quoteType,
                parentType: updatedBinding.parentType,
                parentQuoteType: updatedBinding.parentQuoteType,
                siblingSeparator: updatedBinding.siblingSeparator,
                isSibling: true,
              })
            : undefined,
        };
      })
    : [];
}

/* Define the binding shape for prop types separately since it's recursive */

// Binding props used by all bindings
const baseBindingShape = {
  /**
   * The string shown in the suggestions menu and used to match the user input
   * against.
   */
  label: t.string.isRequired,
  /**
   * The value to inject when a suggestion is picked. If omitted, the label
   * will be used.
   */
  value: t.string,
  /** Short text to show next to the suggestion. */
  detail: t.string,
  /** Long description to show in a tooltip when the suggestion is highlighted. */
  info: t.string,
  /**
   * Name of section used to group options. Can be a string, or an object with
   * a name and rank, where the rank determines the order of the section among
   * other sections. Sections without a rank use the value 1e9.
   */
  section: t.oneOfType([t.string, t.shape({ name: t.string, rank: t.number })]),
  /**
   * A numeric value to boost the order of this suggestion. A positive value
   * will move it earlier in the list than its default sort order, and a
   * negative value will move it later in the list.
   */
  boost: t.number,
};
// Binding props used by all bindings except siblings
const bindingShape = {
  ...baseBindingShape,
  /**
   * A snippet to insert instead of just text. Any content wrapped in `${}`
   * inside the snippet will be highlighted and iterated over using the tab key
   * so each piece can be replaced.
   * When snippet is provided, value, type, children, and siblings params are
   * ignored, and no further chaining is done.
   */
  snippet: t.string,
  /**
   * The type of suggestion, which will determine if there are any parens,
   * brackets, etc. added after the item is inserted.
   * Defaults to 'constant'.
   */
  type: t.oneOf(['function', 'object', 'dot-object', 'constant']),
  /**
   * Type of quotation character to use for functions and objects.
   * Defaults to 'single'.
   */
  quoteType: t.oneOf(['single', 'double', 'none']),
  /**
   * Type of separator used for siblings of this binding.
   * Defaults to ':'.
   */
  siblingSeparator: t.oneOf([':', '[]']),
  /**
   * If set to true and siblings exist, the siblings will be combined with
   * their parent and offered as a single level of suggestions.
   * Defaults to false.
   */
  collapseSiblings: t.bool,
};
// Add recursive children and sibling bindings
bindingShape.siblings = t.arrayOf(t.shape(baseBindingShape));
bindingShape.children = t.arrayOf(t.shape(bindingShape));

CodeEditor.propTypes = {
  /** Language of the editor. */
  language: t.oneOf([
    'js',
    'js-expression',
    'js-template',
    'json',
    'html',
    'ruby',
    'ruby-expression',
    'ruby-template',
    'erb',
    'sql',
    'postgresql',
    'mssql',
    'xml',
    'text',
    'none',
  ]),
  /** Initial value of the editor. */
  value: t.string,
  /**
   * Event callback anytime the content of the editor is changed. If the
   * onChange prop is not provided, the editor will be rendered in disabled
   * mode.
   */
  onChange: t.func,
  /** Event callback anytime the editor's text area gains focus. */
  onFocus: t.func,
  /**
   * The editor uses an external library which prevents us from using a React
   * ref to implement autofocus. For consistency of props, we're using the same
   * name for this prop, but setting any truthy value will make the editor
   * gain focus on render, or whenever this value is changed to truthy.
   */
  focusRef: t.any,
  /** Event callback anytime the editor's text area loses focus. */
  onBlur: t.func,
  /** Unique id that will be set on the wrapper element. */
  id: t.string,
  /** Array of bindings that will be available as suggestions in the editor. */
  bindings: t.arrayOf(t.shape(bindingShape)),
  /** Is the editor disabled */
  disabled: t.bool,
  /** Placeholder text to shown in the editor when there is no value */
  placeholder: t.string,
  /**
   * Defines whether a copy button will be added to the info panel. If omitted,
   * a copy button is added to disabled editors.
   */
  allowCopy: t.bool,
  /**
   * Should the editor render in simple mode, which will hide the gutters and
   * panels, and only show an expand button on the right side of the field.
   * This is useful for making the editor look like a small input.
   */
  simple: t.bool,
  /** The dialog title text to show when the editor is expanded. */
  title: t.string,
  /** Additional classes to add to the editor element. */
  className: t.string,
  /** Additional classes to add to the wrapper element. */
  wrapperClassName: t.string,
};
